import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import styled from 'styled-components';
import {
    RebelColors,
    RebelButton,
    RebelAccentButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelLabel,
    RebelInput,
    RebelTextarea,
    RebelSelect,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB, generateGUID } from '../../../utils/database/firebase-budget-tool';
import { listenToCollection, setDocument, deleteDocument } from '../../../utils/firestore-functions';
import { uploadToSpaces, deleteFromSpaces, generateUniqueFilename, validateSpacesConfig } from '../../../utils/digitalOceanSpaces';

const RebelVillaActivitiesTab = ({ villaId, villaName, devMode }) => {
    const [activities, setActivities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingActivity, setEditingActivity] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [deleteActivityModal, setDeleteActivityModal] = useState(false);
    const [activityToDelete, setActivityToDelete] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [spacesConfigValid] = useState(() => validateSpacesConfig());
    const [activityFilters, setActivityFilters] = useState({
        searchTerm: '',
        category: ''
    });

    const [formData, setFormData] = useState({
        activity_id: '',
        activity_name: '',
        category: '',
        sub_category: '',
        description: '',
        price_per_person: 0,
        flat_price: 0,
        duration_hours: 0,
        max_participants: 0,
        min_participants: 0,
        image_url: '',
        is_active: true,
        villa_id: villaId
    });

    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribeActivities = listenToCollection(budgetDB, "rebel-villa-activities", (data) => {
            const villaActivities = (data || []).filter(act => act.villa_id === villaId);
            setActivities(villaActivities);
            setLoading(false);
        });

        const unsubscribeCategories = listenToCollection(budgetDB, "agenda-block-types", (data) => {
            if (data && data.length > 0) {
                const sortedCategories = data.sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    return (a.name || '').localeCompare(b.name || '');
                });
                setCategories(sortedCategories);
            } else {
                setCategories([]);
            }
        });

        return () => {
            unsubscribeActivities();
            unsubscribeCategories();
        };
    }, [villaId, budgetDB]);

    const resetFormData = () => {
        setFormData({
            activity_id: generateGUID(),
            activity_name: '',
            category: '',
            sub_category: '',
            description: '',
            price_per_person: 0,
            flat_price: 0,
            duration_hours: 0,
            max_participants: 0,
            min_participants: 0,
            image_url: '',
            is_active: true,
            villa_id: villaId
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFilterChange = (field, value) => {
        setActivityFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const openActivityModal = (activity = null) => {
        if (activity) {
            setFormData(activity);
            setEditingActivity(activity);
        } else {
            resetFormData();
            setEditingActivity(null);
        }
        setShowActivityModal(true);
    };

    const closeActivityModal = () => {
        setShowActivityModal(false);
        setEditingActivity(null);
        resetFormData();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const activityId = editingActivity?.id || generateGUID();
            const dataToSave = {
                ...formData,
                id: activityId,
                villa_id: villaId,
                updated_at: new Date(),
                price_per_person: parseFloat(formData.price_per_person) || 0,
                flat_price: parseFloat(formData.flat_price) || 0,
                duration_hours: parseFloat(formData.duration_hours) || 0,
                max_participants: parseInt(formData.max_participants) || 0,
                min_participants: parseInt(formData.min_participants) || 0
            };

            if (!editingActivity) {
                dataToSave.created_at = new Date();
            }

            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "rebel-villa-activities",
                    activityId,
                    dataToSave,
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            closeActivityModal();
        } catch (error) {
            console.error('Error saving activity:', error);
            alert('Error saving activity. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteActivity = (activity) => {
        setActivityToDelete(activity);
        setDeleteActivityModal(true);
    };

    const handleDeleteActivity = async () => {
        if (activityToDelete) {
            await deleteDocument(budgetDB, "rebel-villa-activities", activityToDelete.id);
            setDeleteActivityModal(false);
            setActivityToDelete(null);
        }
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category?.name || categoryId || 'N/A';
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!spacesConfigValid) {
            console.error('Spaces config invalid. Environment variables:', {
                hasAccessKey: !!process.env.REACT_APP_DO_SPACES_ACCESS_KEY,
                hasSecretKey: !!process.env.REACT_APP_DO_SPACES_SECRET_KEY,
                hasBucket: !!process.env.REACT_APP_DO_SPACES_BUCKET,
                bucket: process.env.REACT_APP_DO_SPACES_BUCKET
            });
            alert('Digital Ocean Spaces is not configured. Please check environment variables.');
            return;
        }

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Image must be less than 5MB.');
            return;
        }

        setUploadingImage(true);
        try {
            const key = generateUniqueFilename(file.name, `rebel/activities/${villaId}/`);
            console.log('Uploading image with key:', key);
            const imageUrl = await uploadToSpaces(file, key, {
                ContentType: file.type,
                CacheControl: 'max-age=31536000'
            });
            console.log('Image uploaded successfully:', imageUrl);
            handleChange('image_url', imageUrl);
        } catch (error) {
            console.error('Image upload failed:', error);
            console.error('Error details:', {
                message: error.message,
                name: error.name,
                stack: error.stack
            });
            alert(`Failed to upload image: ${error.message || 'Unknown error'}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = async () => {
        const imageUrl = formData.image_url;
        if (!imageUrl) return;

        try {
            if (imageUrl.includes('digitaloceanspaces.com') && spacesConfigValid) {
                const key = imageUrl.split('.digitaloceanspaces.com/')[1];
                if (key) {
                    await deleteFromSpaces(key);
                }
            }
        } catch (error) {
            console.error('Failed to delete image from storage:', error);
        }
        handleChange('image_url', '');
    };

    const getFilteredActivities = () => {
        return activities.filter(activity => {
            if (activityFilters.searchTerm) {
                const searchLower = activityFilters.searchTerm.toLowerCase();
                const matchesName = activity.activity_name?.toLowerCase().includes(searchLower);
                const matchesDescription = activity.description?.toLowerCase().includes(searchLower);
                if (!matchesName && !matchesDescription) return false;
            }

            if (activityFilters.category && activity.category !== activityFilters.category) {
                return false;
            }

            return true;
        });
    };

    const filteredActivities = getFilteredActivities();

    if (loading) {
        return (
            <LoadingContainer>
                <p>Loading activities...</p>
            </LoadingContainer>
        );
    }

    return (
        <React.Fragment>
            <FormContainer>
                <HeaderRow>
                    <SectionTitle>Activities for {villaName || 'this Property'}</SectionTitle>
                    <RebelAccentButton onClick={() => openActivityModal()}>
                        <RebelIcon icon={['fas', 'plus']} />
                        Add Activity
                    </RebelAccentButton>
                </HeaderRow>

                <FilterContainer>
                    <FilterRow>
                        <FilterInput
                            type="text"
                            placeholder="Search activities..."
                            value={activityFilters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                        />
                        <FilterSelect
                            value={activityFilters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </FilterSelect>
                    </FilterRow>
                </FilterContainer>

                {filteredActivities.length === 0 && activities.length > 0 ? (
                    <EmptyState>
                        <RebelIcon icon={['fas', 'filter']} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                        <p>No activities match your filters.</p>
                    </EmptyState>
                ) : activities.length === 0 ? (
                    <EmptyState>
                        <RebelIcon icon={['fas', 'calendar-day']} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                        <p>No activities yet. Click "Add Activity" to create one.</p>
                    </EmptyState>
                ) : (
                    <ActivitiesGrid>
                        {filteredActivities.map((activity) => (
                            <ActivityCard key={activity.id}>
                                <ActivityImageContainer>
                                    {activity.image_url ? (
                                        <ActivityImage src={activity.image_url} alt={activity.activity_name} />
                                    ) : (
                                        <ActivityImagePlaceholder>
                                            <RebelIcon icon={['fas', 'image']} style={{ fontSize: '32px', opacity: 0.3 }} />
                                        </ActivityImagePlaceholder>
                                    )}
                                </ActivityImageContainer>
                                <ActivityContent>
                                    <ActivityTitle>{activity.activity_name || 'Unnamed Activity'}</ActivityTitle>
                                    <ActivityInfo>
                                        <InfoItem>
                                            <RebelIcon icon={['fas', 'tag']} />
                                            <span>{getCategoryName(activity.category)}</span>
                                        </InfoItem>
                                        <InfoItem>
                                            <RebelIcon icon={['fas', 'clock']} />
                                            <span>{activity.duration_hours ? `${activity.duration_hours}h` : 'N/A'}</span>
                                        </InfoItem>
                                        <InfoItem>
                                            <RebelIcon icon={['fas', 'users']} />
                                            <span>{activity.min_participants || 0} - {activity.max_participants || 0}</span>
                                        </InfoItem>
                                        <InfoItem>
                                            <RebelIcon icon={['fas', 'dollar-sign']} />
                                            <span>
                                                {activity.price_per_person ? `$${activity.price_per_person}/person` : ''}
                                                {activity.price_per_person && activity.flat_price ? ' + ' : ''}
                                                {activity.flat_price ? `$${activity.flat_price} flat` : ''}
                                                {!activity.price_per_person && !activity.flat_price ? 'N/A' : ''}
                                            </span>
                                        </InfoItem>
                                    </ActivityInfo>
                                    <ActivityActions>
                                        <RebelButton onClick={() => openActivityModal(activity)} size="sm">
                                            <RebelIcon icon={['fas', 'edit']} />
                                            Edit
                                        </RebelButton>
                                        <RebelDangerButton onClick={() => confirmDeleteActivity(activity)} size="sm">
                                            <RebelIcon icon={['fas', 'trash']} />
                                            Delete
                                        </RebelDangerButton>
                                    </ActivityActions>
                                    {devMode && (
                                        <ActivityDevInfo>
                                            <DevInfoRow>
                                                <DevInfoLabel>Activity ID:</DevInfoLabel>
                                                <DevInfoValue>{activity.id}</DevInfoValue>
                                            </DevInfoRow>
                                            <DevInfoRow>
                                                <DevInfoLabel>Category ID:</DevInfoLabel>
                                                <DevInfoValue>{activity.category || '(none)'}</DevInfoValue>
                                            </DevInfoRow>
                                            <DevInfoRow>
                                                <DevInfoLabel>Villa ID:</DevInfoLabel>
                                                <DevInfoValue>{activity.villa_id}</DevInfoValue>
                                            </DevInfoRow>
                                        </ActivityDevInfo>
                                    )}
                                </ActivityContent>
                            </ActivityCard>
                        ))}
                    </ActivitiesGrid>
                )}
            </FormContainer>

            {/* Activity Modal */}
            <LargeRebelModal isOpen={showActivityModal} toggle={closeActivityModal} size="xl">
                <RebelModalHeader toggle={closeActivityModal}>
                    {editingActivity ? 'Edit Activity' : 'Add New Activity'}
                </RebelModalHeader>
                <RebelModalBody>
                    <ModalFormContainer>
                        <FormGroup>
                            <RebelLabel>Activity Name *</RebelLabel>
                            <RebelInput
                                type="text"
                                value={formData.activity_name}
                                onChange={(e) => handleChange('activity_name', e.target.value)}
                                placeholder="e.g., Guided Nature Walk"
                            />
                        </FormGroup>

                        <FormGroup>
                            <RebelLabel>Category *</RebelLabel>
                            <RebelSelect
                                value={formData.category}
                                onChange={(e) => handleChange('category', e.target.value)}
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </RebelSelect>
                        </FormGroup>

                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <RebelLabel>Price per Person ($)</RebelLabel>
                                    <RebelInput
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.price_per_person}
                                        onChange={(e) => handleChange('price_per_person', e.target.value)}
                                        placeholder="0"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <RebelLabel>Flat Price ($)</RebelLabel>
                                    <RebelInput
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.flat_price}
                                        onChange={(e) => handleChange('flat_price', e.target.value)}
                                        placeholder="0"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <RebelLabel>Duration (hrs)</RebelLabel>
                                    <RebelInput
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={formData.duration_hours}
                                        onChange={(e) => handleChange('duration_hours', e.target.value)}
                                        placeholder="0"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <RebelLabel>Min Participants</RebelLabel>
                                    <RebelInput
                                        type="number"
                                        min="0"
                                        value={formData.min_participants}
                                        onChange={(e) => handleChange('min_participants', e.target.value)}
                                        placeholder="1"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <RebelLabel>Max Participants</RebelLabel>
                                    <RebelInput
                                        type="number"
                                        min="0"
                                        value={formData.max_participants}
                                        onChange={(e) => handleChange('max_participants', e.target.value)}
                                        placeholder="20"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <RebelLabel>Description</RebelLabel>
                            <RebelTextarea
                                rows="3"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Detailed description of the activity..."
                            />
                        </FormGroup>

                        <FormGroup>
                            <RebelLabel>Activity Image</RebelLabel>
                            {!formData.image_url ? (
                                <ImageUploadArea>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        disabled={uploadingImage || !spacesConfigValid}
                                        style={{ display: 'none' }}
                                        id="activity-image-upload"
                                    />
                                    <label htmlFor="activity-image-upload">
                                        <RebelIcon icon={['fas', 'cloud-upload-alt']} />
                                        <span>{uploadingImage ? 'Uploading...' : 'Click to upload image'}</span>
                                        <small>JPG, PNG, GIF up to 5MB</small>
                                    </label>
                                </ImageUploadArea>
                            ) : (
                                <ImagePreviewCard>
                                    <ImagePreviewCardImage>
                                        <img src={formData.image_url} alt="Activity preview" />
                                        <ImageRemoveButton onClick={handleRemoveImage} type="button">
                                            <RebelIcon icon={['fas', 'times']} />
                                        </ImageRemoveButton>
                                    </ImagePreviewCardImage>
                                    <ImagePreviewCardLabel>Preview</ImagePreviewCardLabel>
                                </ImagePreviewCard>
                            )}
                            {!spacesConfigValid && (
                                <UploadWarning>Image upload not available - storage not configured</UploadWarning>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <ToggleContainer>
                                <RebelLabel>Status</RebelLabel>
                                <ToggleSwitch>
                                    <ToggleInput
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => handleChange('is_active', e.target.checked)}
                                    />
                                    <ToggleSlider />
                                </ToggleSwitch>
                                <ToggleLabel>{formData.is_active ? 'Active' : 'Inactive'}</ToggleLabel>
                            </ToggleContainer>
                        </FormGroup>

                        <ButtonContainer>
                            <RebelSecondaryButton onClick={closeActivityModal} disabled={saving}>
                                Cancel
                            </RebelSecondaryButton>
                            <RebelAccentButton
                                onClick={handleSave}
                                disabled={saving || !formData.activity_name || !formData.category}
                            >
                                {saving ? 'Saving...' : editingActivity ? 'Update Activity' : 'Create Activity'}
                            </RebelAccentButton>
                        </ButtonContainer>
                    </ModalFormContainer>
                </RebelModalBody>
            </LargeRebelModal>

            {/* Delete Activity Modal */}
            <RebelModal isOpen={deleteActivityModal} toggle={() => setDeleteActivityModal(false)}>
                <RebelModalHeader toggle={() => setDeleteActivityModal(false)}>
                    Confirm Delete
                </RebelModalHeader>
                <RebelModalBody>
                    Are you sure you want to delete this activity?
                    <br /><br />
                    <strong>{activityToDelete?.activity_name || 'This activity'}</strong>
                    <br /><br />
                    This action cannot be undone.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={() => setDeleteActivityModal(false)}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelDangerButton onClick={handleDeleteActivity}>
                        <RebelIcon icon={['fas', 'trash']} />
                        Delete
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>
        </React.Fragment>
    );
};

export default RebelVillaActivitiesTab;

const LoadingContainer = styled.div`
    text-align: center;
    padding: 48px;
    color: ${RebelColors.primaryMuted};
`;

const FormContainer = styled.div`
    padding: 24px;
`;

const SectionTitle = styled.h5`
    color: ${RebelColors.gold};
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 0;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid ${RebelColors.gold}40;
`;

const FilterContainer = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
`;

const FilterRow = styled.div`
    display: flex;
    gap: 12px;
`;

const FilterInput = styled.input`
    flex: 1;
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    padding: 10px 16px;
    border-radius: 6px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
        background: ${RebelColors.surface};
    }

    &::placeholder {
        color: ${RebelColors.primaryMuted};
    }
`;

const FilterSelect = styled.select`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.text};
    padding: 10px 16px;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
        background: ${RebelColors.surface};
    }

    option {
        background: ${RebelColors.surface};
        color: ${RebelColors.text};
    }
`;

const ActivitiesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
`;

const ActivityCard = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 215, 0, 0.15);
        border-color: ${RebelColors.gold}80;
    }
`;

const ActivityImageContainer = styled.div`
    width: 100%;
    height: 150px;
    background: ${RebelColors.background};
`;

const ActivityImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ActivityImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${RebelColors.background};
`;

const ActivityContent = styled.div`
    padding: 16px;
`;

const ActivityTitle = styled.h6`
    color: ${RebelColors.text};
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 12px 0;
`;

const ActivityInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${RebelColors.primaryMuted};
    font-size: 12px;

    svg {
        width: 14px;
        color: ${RebelColors.gold};
        font-size: 11px;
    }
`;

const ActivityActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 12px;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: ${RebelColors.primaryMuted};
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 2px dashed ${RebelColors.border};

    p {
        margin-top: 16px;
        font-style: italic;
    }
`;

const LargeRebelModal = styled(RebelModal)`
    .modal-dialog {
        max-width: 900px;
    }

    .modal-content {
        min-height: auto;
    }
`;

const ModalFormContainer = styled.div``;

const FormGroup = styled.div`
    margin-bottom: 1rem;

    label {
        display: block;
        margin-bottom: 0.5rem;
    }

    select {
        height: 46px;
        width: 100%;
    }

    input[type="text"],
    input[type="number"] {
        height: 46px;
        width: 100%;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid ${RebelColors.border};
`;

const ImageUploadArea = styled.div`
    border: 2px dashed ${RebelColors.border};
    border-radius: 8px;
    padding: 32px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${RebelColors.gold};
        background: ${RebelColors.gold}10;
    }

    label {
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: ${RebelColors.primaryMuted};
    }

    svg {
        font-size: 32px;
        color: ${RebelColors.gold};
    }

    small {
        font-size: 12px;
        opacity: 0.7;
    }
`;

const ImagePreviewCard = styled.div`
    margin-top: 12px;
    max-width: 300px;
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 12px;
    overflow: hidden;
`;

const ImagePreviewCardImage = styled.div`
    position: relative;
    width: 100%;
    height: 150px;
    background: ${RebelColors.background};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ImagePreviewCardLabel = styled.div`
    padding: 8px 12px;
    font-size: 12px;
    color: ${RebelColors.primaryMuted};
    text-align: center;
    border-top: 1px solid ${RebelColors.border};
`;

const ImageRemoveButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #000;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
        transform: scale(1.1);
    }

    svg {
        font-size: 14px;
        color: ${RebelColors.danger};
        width: 14px;
        height: 14px;
        margin: 0 !important;
    }
`;

const UploadWarning = styled.small`
    display: block;
    margin-top: 8px;
    color: ${RebelColors.warning};
`;

const ToggleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;

    > label {
        margin-bottom: 0;
        line-height: 1;
    }
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    height: 26px;
`;

const ToggleInput = styled.input`
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
        background: linear-gradient(135deg, ${RebelColors.gold} 0%, #d4af37 100%);
    }

    &:checked + span:before {
        transform: translateX(24px);
    }
`;

const ToggleSlider = styled.span`
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 34px;
    transition: 0.3s;

    &:before {
        content: "";
        position: absolute;
        height: 20px;
        width: 20px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        transition: 0.3s;
    }
`;

const ToggleLabel = styled.span`
    color: ${RebelColors.text};
    font-size: 14px;
    line-height: 1;
    display: inline-block;
`;

const ActivityDevInfo = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #4a4a4a;
    font-family: monospace;
`;

const DevInfoRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DevInfoLabel = styled.span`
    color: #888;
    font-size: 10px;
    min-width: 80px;
`;

const DevInfoValue = styled.span`
    color: #00ff88;
    font-size: 10px;
    background-color: #0a0a15;
    padding: 2px 6px;
    border-radius: 4px;
    user-select: all;
    word-break: break-all;
`;
